import { useState } from 'react'

import {
  ApprovalsMutationsCreateApprovalsApprovalFlowArgs as ApprovalFlowArgs,
  CreateApprovalsApprovalFlowApproversInput as ApproverArgs,
  CreateApprovalsApprovalFlowStepsInput as StepArgs,
} from 'generated/resources/graphql'

import { defaultApprovalFlow } from '../lib/approvalFlows'

export const useApprovalFlowForm = (approvalFlow: ApprovalFlowArgs | null) => {
  const [approvalFlowData, setApprovalFlowData] = useState<ApprovalFlowArgs>(
    approvalFlow ?? defaultApprovalFlow
  )

  const approverExists = (approver: ApproverArgs, approvers: ApproverArgs[]) => {
    return approvers.some((existingApprover) => {
      if (approver.strategy === 'members') {
        return (
          existingApprover.strategy === approver.strategy &&
          existingApprover.accessId === approver.accessId
        )
      }
      return existingApprover.strategy === approver.strategy
    })
  }

  const addApproverToStep = (stepPosition: number, approver: ApproverArgs) => {
    const newSteps = approvalFlowData.steps.map((step) => {
      if (step.position === stepPosition && !approverExists(approver, step.approvers)) {
        return { ...step, approvers: [...step.approvers, approver] }
      }
      return step
    })

    setApprovalFlowData({ ...approvalFlowData, steps: newSteps })
  }

  const removeApproverFromStep = (stepPosition: number, approverIndex: number) => {
    const newSteps = approvalFlowData.steps.map((step) => {
      if (step.position === stepPosition) {
        const approverId = step.approvers[approverIndex]?.id
        return {
          ...step,
          approverIdsToBeDeleted: approverId
            ? [...(step.approverIdsToBeDeleted ?? []), approverId]
            : step.approverIdsToBeDeleted ?? [],
          approvers: step.approvers.filter((_, index) => index !== approverIndex),
        }
      }
      return step
    })

    setApprovalFlowData({ ...approvalFlowData, steps: newSteps })
  }

  const addStep = (approver: ApproverArgs) => {
    const newStep: StepArgs = {
      id: null,
      position: approvalFlowData.steps.length,
      approvers: [approver],
    }

    setApprovalFlowData({ ...approvalFlowData, steps: [...approvalFlowData.steps, newStep] })
  }

  const updateStep = (step: StepArgs) => {
    const newSteps = approvalFlowData.steps.map((existingStep) =>
      existingStep.position === step.position ? step : existingStep
    )

    setApprovalFlowData({ ...approvalFlowData, steps: newSteps })
  }

  const deleteStep = (deletedStep: StepArgs) => {
    const newSteps = approvalFlowData.steps
      .filter((step) => step.position !== deletedStep.position)
      .map((step, index) => ({ ...step, position: index }))

    const stepIdsToBeDeleted = deletedStep.id
      ? [...approvalFlowData.stepIdsToBeDeleted, deletedStep.id]
      : approvalFlowData.stepIdsToBeDeleted

    setApprovalFlowData({ ...approvalFlowData, steps: newSteps, stepIdsToBeDeleted })
  }

  return {
    approvalFlowData,
    addApproverToStep,
    removeApproverFromStep,
    addStep,
    updateStep,
    deleteStep,
  }
}
